import { ComponentDataSourceService } from '../../component-data-source.service';
import { Observable, map, of, take } from 'rxjs';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { EventParticipantDTO } from '../../../../dto/event/event-participant.dto';
import { EventDTO } from '../../../../dto/event/event.dto';
import { Router } from '@angular/router';
import { ComponentDataDestService } from '../../../component-data-dest/component-data-dest.service';
import * as DataActions from '../../../../ngrx/data/data.actions';
import { isAfbudParticipationStatus } from '../../../../constants/event-participant-status.utils';
import {
  eventParticipantDisplayTotalCount,
  filterParticipantDisplayItemsBySearch,
} from './event-participant-search.util';

// Interface for display purposes only - flattened structure for the table
interface ParticipantDisplayData extends EventParticipantDTO {
  name: string; // Combined first_name and last_name
  email: string;
  organization: string; // Org name from user.locations or form data
  maintenance_minutes: number;
  // Dynamic module maintenance minutes properties will be added at runtime
  [key: string]: any;
}

// Module information for display
interface ModuleInfo {
  id: number;
  name: string;
  maintenance_minutes: number;
}

export interface EventParticipantDataSourceConfig {
  name: string;
  description: string;
  participantFilter: (participant: EventParticipantDTO) => boolean;
  /** Same columns as venteliste: no maintenance/module columns, matching widths */
  columnProfile?: 'full' | 'compact';
}

export const createEventParticipantDataSource = (
  service: ComponentDataSourceService,
  config: EventParticipantDataSourceConfig
): DataSourceFunction => {
  // Keep track of modules for the table columns
  let eventModules: ModuleInfo[] = [];
  let showModuleColumns = false; // Default to false to hide module columns
  let lastEventId: string | null = null;

  // Event organization info for cross-org detection in subtitles
  let eventOrganizationId: number | null = null;

  // Whether to show the organisation column (set after data loads)
  let showOrganizationColumn = false;
  
  
  // Reference to dynamic table columns
  let tableColumns: any[] = [
    { 
      key: 'name',
      header: 'Navn', 
      type: 'text',
      width: '250px',
      transform: (value: string) => value || '-',
      subtitle: (rowData: ParticipantDisplayData) => {
        const lines: string[] = [];

        // Location name for registered users — org is shown in its own column
        if (rowData.user?.locations && rowData.user.locations.length > 0) {
          const locName = rowData.user.locations[0].locations_name;
          if (locName) lines.push(locName);
        }

        // Comments
        const commentParts: string[] = [];
        if (rowData.data?.comments && rowData.data.comments.trim()) {
          commentParts.push(rowData.data.comments.trim());
        }
        if (rowData.data?.additional_comments &&
            rowData.data.additional_comments.trim() &&
            rowData.data.additional_comments !== rowData.data.comments) {
          commentParts.push(rowData.data.additional_comments.trim());
        }
        if (commentParts.length > 0) {
          lines.push(commentParts.join(' • '));
        }

        const cateringPrefs = rowData.data?.catering_preferences;
        const dietaryPrefs = rowData.data?.dietary_preferences;
        const prefLine =
          cateringPrefs && Array.isArray(cateringPrefs) && cateringPrefs.length > 0
            ? cateringPrefs.join(', ')
            : dietaryPrefs && Array.isArray(dietaryPrefs) && dietaryPrefs.length > 0
              ? dietaryPrefs.join(', ')
              : null;
        if (prefLine) {
          lines.push(`Catering: ${prefLine}`);
        }

        // Added by
        if (rowData.data?.added_by) {
          lines.push(`Tilmeldt af: ${rowData.data.added_by.name}`);
        }

        return lines.length > 0 ? lines.join('\n') : null;
      }
    },
    { 
      key: 'email',
      header: 'Email', 
      type: 'text',
      width: '250px',
      transform: (value: string) => {
        return value || '-';
      }
    },
    { 
      key: 'role', 
      header: 'Rolle', 
      type: 'select',
      width: '120px',
      editable: true,
      editor: 'select',
      options: [
        { value: 'attendee', label: 'Deltager' },
        { value: 'instructor', label: 'Instruktør' }
      ],
      onChange: (newValue: string, rowData: ParticipantDisplayData) => {
        if (rowData.participant_id && rowData.participant_id > 0) {
          const router = service.injector.get(Router);
          const urlWithoutQuery = (router.url || '').split('?')[0];
          const pathSegments = urlWithoutQuery.split('/');
          const eventGuid = pathSegments.find((segment: string) =>
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
          );
          
          // Call the update_event_participation data destination
          service.injector.get(ComponentDataDestService).getDataDest('update_event_participation')?.({
            eventGuid,
            user_id: rowData.participant_id,
            user_uuid: rowData.user?.uuid,
            role: newValue,
            status: rowData.status,
            // Send maintenance minutes data if completed
            maintenance_minutes: rowData.status === 'completed' ? rowData.maintenance_minutes : 0,
            module_maintenance_minutes: rowData.status === 'completed' ? 
              eventModules.map(m => ({
                module_id: m.id,
                minutes: rowData[`module_${m.id}_minutes`] !== undefined ? 
                  Number(rowData[`module_${m.id}_minutes`]) : 
                  m.maintenance_minutes
              })) : []
          });
        }
      },
      transform: (value: string) => {
        const roles: Record<string, string> = {
          'attendee': 'Deltager',
          'instructor': 'Instruktør',
          'assistant': 'Assistent',
          'empty': '-'
        };
        return roles[value || 'empty'] || value || '-';
      }
    },
    { 
      key: 'status', 
      header: 'Status', 
      type: 'select',
      width: '180px',
      editable: true,
      editor: 'select',
      options: [
        { value: 'waitlist', label: 'Venteliste' },
        { value: 'pending', label: 'Tilmeldt' },
        { value: 'shownup', label: 'Mødt op' },
        { value: 'requires_attention', label: 'Kræver opmærksomhed' },
        { value: 'completed', label: 'Gennemført' },
        { value: 'not_appear', label: 'Udeblevet' },
        { value: 'cancellation', label: 'Afbud' },
        { value: 'not_participant', label: 'Ikke deltager' }
      ],
      onChange: (newValue: string, rowData: ParticipantDisplayData) => {
        if (rowData.participant_id && rowData.participant_id > 0) {
          const router = service.injector.get(Router);
          const urlWithoutQuery = (router.url || '').split('?')[0];
          const pathSegments = urlWithoutQuery.split('/');
          const eventGuid = pathSegments.find((segment: string) =>
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
          );
          
          // Call the update_event_participation data destination
          service.injector.get(ComponentDataDestService).getDataDest('update_event_participation')?.({
            eventGuid,
            user_id: rowData.participant_id,
            user_uuid: rowData.user?.uuid,
            role: rowData.role,
            status: newValue,
            // Send maintenance minutes data if completed
            maintenance_minutes: newValue === 'completed' ? rowData.maintenance_minutes : 0,
            module_maintenance_minutes: newValue === 'completed' ? 
              eventModules.map(m => ({
                module_id: m.id,
                minutes: rowData[`module_${m.id}_minutes`] !== undefined ? 
                  Number(rowData[`module_${m.id}_minutes`]) : 
                  m.maintenance_minutes
              })) : []
          });
        }
      },
      transform: (value: string) => {
        const statuses: Record<string, string> = {
          'waitlist': 'Venteliste',
          'pending': 'Tilmeldt',
          'shownup': 'Mødt op',
          'requires_attention': 'Kræver opmærksomhed',
          'completed': 'Gennemført',
          'not_appear': 'Udeblevet',
          'cancellation': 'Afbud',
          'not_participant': 'Ikke deltager',
          'empty': '-'
        };
        return statuses[value || 'empty'] || value || '-';
      }
    },
    { 
      key: 'maintenance_minutes', 
      header: 'Vedligeholdelsesminutter', 
      type: 'text',
      editable: false,
      width: '180px',
      transform: (value: number) => {
        return value || 0;
      }
    }
  ];
  
  const compactParticipantColumn = (col: any): any => {
    if (!col) return col;
    if (col.key === 'name') {
      return col;
    }
    const { width: _width, ...rest } = col;
    return rest;
  };

  // Function to update the table columns based on module visibility
  const updateTableColumns = () => {
    if (config.columnProfile === 'compact') {
      const compactKeys = ['name', 'email', 'role', 'status'];
      const columns = compactKeys
        .map((key) => compactParticipantColumn(tableColumns.find((c) => c.key === key)))
        .filter(Boolean);

      if (showOrganizationColumn) {
        const nameIdx = columns.findIndex((c: any) => c.key === 'name');
        if (nameIdx !== -1) {
          columns.splice(nameIdx + 1, 0, {
            key: 'organization',
            header: 'Organisation',
            type: 'text',
            width: '160px',
            transform: (value: string) => value || '-',
          });
        }
      }

      return columns;
    }

    // Start with the base columns, hiding maintenance_minutes when no modules exist
    const columns = eventModules.length > 0
      ? [...tableColumns]
      : tableColumns.filter(c => c.key !== 'maintenance_minutes');

    // Insert organisation column after name when at least one participant has org data
    if (showOrganizationColumn) {
      const nameIdx = columns.findIndex((c: any) => c.key === 'name');
      if (nameIdx !== -1) {
        columns.splice(nameIdx + 1, 0, {
          key: 'organization',
          header: 'Organisation',
          type: 'text',
          width: '160px',
          transform: (value: string) => value || '-'
        });
      }
    }
    
    
    // Always add module columns if they exist, but mark them dynamically
    // The component will show/hide them based on the tableOptions toggle state
    if (eventModules.length > 0) {
      
      eventModules.forEach(module => {
        const moduleColumn = {
          key: `module_${module.id}_minutes`,
          header: module.name + ' minutter',
          type: 'text',
          editable: true,
          editor: 'text',
          width: '180px',
          dynamicallyAdded: true,
          isModuleColumn: true, // Special flag to identify module columns
                    onChange: (newValue: string, rowData: ParticipantDisplayData) => {
            
            try {
              // Update the module value in the row data (local UI update only)
              const moduleKey = `module_${module.id}_minutes`;
              rowData[moduleKey] = Number(newValue);
              
              // Update total maintenance minutes (local UI update only)
              let total = 0;
              eventModules.forEach(m => {
                // Use the specific module value if set, otherwise use the module's default
                const moduleValue = 
                  rowData[`module_${m.id}_minutes`] !== undefined ? 
                  Number(rowData[`module_${m.id}_minutes`]) : 
                  m.maintenance_minutes;
                
                total += moduleValue;
              });
              
              // Update the total maintenance minutes for this participant (local UI update only)
              rowData.maintenance_minutes = total;
              
              // Always send the update to the backend (regardless of status)
              const router = service.injector.get(Router);
              const urlWithoutQuery = (router.url || '').split('?')[0];
              const pathSegments = urlWithoutQuery.split('/');
              const eventGuid = pathSegments.find((segment: string) =>
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
              );
              
              if (eventGuid) {
                
                // Call the update_event_participation data destination for maintenance minutes
                // This should NOT trigger any data reload or UI refresh
                service.injector.get(ComponentDataDestService).getDataDest('update_event_participation')?.({
                  eventGuid,
                  user_id: rowData.participant_id,
                  user_uuid: rowData.user?.uuid,
                  role: rowData.role,
                  status: rowData.status,
                  maintenance_minutes: total,
                  module_maintenance_minutes: eventModules.map(m => ({
                    module_id: m.id,
                    minutes: rowData[`module_${m.id}_minutes`] !== undefined ? 
                      Number(rowData[`module_${m.id}_minutes`]) : 
                      m.maintenance_minutes
                  }))
                });
              } else {
                console.warn(`[Participants] Could not find eventGuid in URL for backend update`);
              }
            } catch (error) {
              console.error(`[Participants] Error in module ${module.id} onChange handler:`, error);
            }
          }
        };
        
        columns.push(moduleColumn);
      });
    }
    
    return columns;
  };

  // Function to directly fetch event data from the API
  const fetchEventData = (eventGuid: string): Observable<EventDTO> => {
    return service.http.get<EventDTO>(`${environment.apiUrl}/events/${eventGuid}`);
  };

  return {
    name: config.name,
    description: config.description,
    execute: (params?: LoadDataParams): Observable<{ items: ParticipantDisplayData[], totalCount: number }> => {
      const router = service.injector.get(Router);
      const currentUrl = router.url || '';
      const urlWithoutQuery = currentUrl.split('?')[0];
      // Clean the URL in the address bar if it contains query params (e.g., ?org=...)
      if (currentUrl.includes('?')) {
        try {
          router.navigateByUrl(urlWithoutQuery, { replaceUrl: true });
        } catch {
          // Ignore navigation cleanup errors
        }
      }
      const pathSegments = urlWithoutQuery.split('/');
      const eventGuid = pathSegments.find(segment => 
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
      );
      
      // Check if sort parameters are in the filters
      if (params?.filters) {
        const sortColumn = params.filters.find(f => f.column === 'sortColumn')?.value as string;
        const sortDirection = params.filters.find(f => f.column === 'sortDirection')?.value as 'asc' | 'desc';
        
        if (sortColumn && sortDirection) {
        }
        
        // Check if module columns option is toggled
        const showModuleOption = params.filters?.find(f => f.column === 'show_module_columns');

        if (showModuleOption !== undefined) {
          showModuleColumns = Boolean(showModuleOption.value);
        } else {
          // Keep the default value - don't automatically enable
        }
      }
      
      if (!eventGuid) {
        throw new Error('No valid event GUID found in URL');
      }

      // Check if we need to refetch event data (new event or first load)
      if (eventGuid !== lastEventId) {
        lastEventId = eventGuid;
      }

      // Directly fetch the event data
      return fetchEventData(eventGuid).pipe(
        map((event: EventDTO) => {
          // Capture event org for cross-org detection in subtitles
          eventOrganizationId = event.organization?.id ?? null;

          const participants = event.participants?.filter((p: EventParticipantDTO) => config.participantFilter(p)) || [];
          
          // Extract modules from event items and calculate total maintenance minutes
          let totalMaintenanceMinutes = 0;
          eventModules = [];
          
          if (event.items && event.items.length > 0) {
            
            const moduleItems = event.items.filter(item => 
              item.item_type === 'modules' && 
              item.item_details && 
              !item.deleted
            );
            
            
            if (moduleItems.length > 0) {
              moduleItems.forEach(item => {
                // Cast to any to safely access properties
                const moduleDetails = item.item_details as any;
                
                // Check if this module ID already exists in eventModules to prevent duplicates
                const existingModule = eventModules.find(m => m.id === item.item_id);
                if (existingModule) {
                  return; // Skip this duplicate module
                }
                
                // Default maintenance minutes value if not present
                const minutes = 30; // Default value
                
                // Make sure we have a proper object
                if (moduleDetails) {
                  const moduleInfo: ModuleInfo = {
                    id: item.item_id,
                    name: moduleDetails.name || `Module ${item.item_id}`,
                    maintenance_minutes: moduleDetails.maintenance_minutes || minutes
                  };
                  
                  eventModules.push(moduleInfo);
                  
                  // Add to total
                  totalMaintenanceMinutes += moduleInfo.maintenance_minutes;
                  
                } else {
                }
              });
              
              
              // Don't automatically enable module columns - respect the default/user setting
            } else {
            }
          }
          
          if (eventModules.length > 0 && showModuleColumns === undefined) {
            // Default to showing module columns if we found modules and no explicit toggle state
            showModuleColumns = true;
          }
          
          // Create display data with flattened properties while preserving original structure
          let items: ParticipantDisplayData[] = participants.length > 0 ? participants.map(p => {
            // Calculate total maintenance minutes based on module minutes
            let calculatedMaintenanceMinutes = 0;
            
            // If we have stored module-specific data, use that to calculate the total
            if (p.data?.maintenance_minutes) {
              // Backend stores maintenance_minutes as an object: { "1": 60, "2": 30 }
              calculatedMaintenanceMinutes = Object.values(p.data.maintenance_minutes).reduce(
                (total: number, minutes: any) => total + (Number(minutes) || 0), 0
              );
            } else {
              // Otherwise use the default module minutes
              calculatedMaintenanceMinutes = totalMaintenanceMinutes;
            }
            
            // Always resolve the full org name — the column visibility decision is made
            // separately below; when it IS shown, every participant should display their org.
            let organizationDisplay = '';
            if (p.user?.locations && p.user.locations.length > 0) {
              organizationDisplay = p.user.locations[0].data?.org_name || '';
            } else {
              // Anonymous / external user: use form-submitted text
              organizationDisplay = p.data?.organization?.trim() || '';
            }

            // Anonymous participants added by another registrant: inherit their org via
            // reverse lookup on the registrant's added_participants list.
            if (!organizationDisplay && p.user?.uuid) {
              const registrant = event.participants?.find(ep =>
                Array.isArray((ep.data as any)?.added_participants) &&
                (ep.data as any).added_participants.some((ap: any) => ap.uuid === p.user?.uuid)
              );
              if (registrant) {
                if (registrant.user?.locations && registrant.user.locations.length > 0) {
                  organizationDisplay = registrant.user.locations[0].data?.org_name || '';
                }
                if (!organizationDisplay) {
                  organizationDisplay = registrant.data?.organization?.trim() || '';
                }
              }
            }

            // Create the base participant object as a completely new mutable object
            const participant: ParticipantDisplayData = JSON.parse(JSON.stringify({
              ...p, // Keep all original properties
              // Add flattened properties for display
              name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim() || '-',
              email: p.user?.user || '-',
              organization: organizationDisplay,
              maintenance_minutes: calculatedMaintenanceMinutes // Always calculated value
            }));
            
            // Ensure all properties are writable
            Object.defineProperty(participant, 'maintenance_minutes', {
              value: calculatedMaintenanceMinutes,
              writable: true,
              enumerable: true,
              configurable: true
            });
            
            
            // Add module-specific maintenance minutes properties if showing module columns
            if (eventModules.length > 0) {
              
              // First check if there's module-specific data stored
              if (p.data?.maintenance_minutes) {
                // Use stored module data - backend stores as object: { "1": 60, "2": 30 }
                eventModules.forEach(module => {
                  const storedMinutes = p.data?.maintenance_minutes?.[module.id.toString()];
                  
                  // Ensure the property is writable by explicitly setting it
                  Object.defineProperty(participant, `module_${module.id}_minutes`, {
                    value: storedMinutes !== undefined ? Number(storedMinutes) : module.maintenance_minutes,
                    writable: true,
                    enumerable: true,
                    configurable: true
                  });
                  
                });
              } else {
                // Use default values
                eventModules.forEach(module => {
                  // Ensure the property is writable by explicitly setting it
                  Object.defineProperty(participant, `module_${module.id}_minutes`, {
                    value: module.maintenance_minutes,
                    writable: true,
                    enumerable: true,
                    configurable: true
                  });
                  
                });
              }
            }
            
            return participant;
          }) : [{
            participant_id: 0,
            event_id: 0,
            user: {
              uuid: '',
              first_name: 'Der er endnu ingen data',
              last_name: '',
              user: '',
              roles: null,
              organizations_id: null,
              locations: null,
              status: null
            },
            role: 'empty',
            status: 'empty',
            joined_at: undefined,
            data: null,
            // Flattened properties for display
            name: 'Der er endnu ingen data',
            email: '',
            organization: '',
            maintenance_minutes: 0
          }];
          
          // Show the organisation column only when at least one participant is from a
          // different org than the event. When the column IS shown, all participants
          // (including same-org ones) display their org so the table stays consistent.
          showOrganizationColumn = items.some(item => {
            const locations = item.user?.locations;
            if (locations && locations.length > 0) {
              const locOrgId: number | null = locations[0]?.data?.organizations_id ?? null;
              return locOrgId !== null && eventOrganizationId !== null && locOrgId !== eventOrganizationId;
            }
            // Anonymous user with form-submitted org text — always counts as cross-org
            return !!(item.data?.organization?.trim());
          });

          items = filterParticipantDisplayItemsBySearch(
            items,
            params?.searchQuery ?? params?.queryParams?.['searchQuery']
          );

          // Sort the items if sort parameters were provided
          if (params?.filters) {
            const sortColumn = params.filters.find(f => f.column === 'sortColumn')?.value as string;
            const sortDirection = params.filters.find(f => f.column === 'sortDirection')?.value as 'asc' | 'desc';
            
            if (sortColumn && sortDirection && items.length > 0) {
              items.sort((a, b) => {
                // Type-safe access to properties
                let aValue: any;
                let bValue: any;
                
                // Handle specific known properties
                switch(sortColumn) {
                  case 'name':
                    aValue = a.name;
                    bValue = b.name;
                    break;
                  case 'email':
                    aValue = a.email;
                    bValue = b.email;
                    break;
                  case 'role':
                    aValue = a.role;
                    bValue = b.role;
                    break;
                  case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                  case 'maintenance_minutes':
                    aValue = a.maintenance_minutes;
                    bValue = b.maintenance_minutes;
                    break;
                  case 'organization':
                    aValue = a.organization;
                    bValue = b.organization;
                    break;
                  default:
                    // For unknown properties, try accessing them but don't guarantee type safety
                    aValue = (a as any)[sortColumn];
                    bValue = (b as any)[sortColumn];
                }
                
                // Handle different data types
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                  return sortDirection === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
                } else {
                  // For non-string values (numbers, etc.)
                  if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                  if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                  return 0;
                }
              });
              
            }
          }
          
          return {
            items,
            totalCount: eventParticipantDisplayTotalCount(items),
          };
        })
      );
    },
    settings: {
      get columns() {
        const cols = updateTableColumns();
        return cols;
      },
      pageSize: config.columnProfile === 'compact' ? 10 : 50,
      pageSizeOptions: [10, 25, 50, 100], // Start with 10 as the first option
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'name',
      defaultSortDirection: 'asc',
      multiSelect: true, // Enable multiselect
      showRemoveButton: true, // Enable remove button
      removeButtonText: 'Slet valgte',
      removeButtonColor: 'warn',
      onRemoveSelected: (selectedItems: any[]) => {
        
        // Extract UUIDs from selected participants
        const participantUuids = selectedItems
          .filter(item => item.user?.uuid) // Only include items with valid UUIDs
          .map(item => item.user.uuid);
        
        if (participantUuids.length === 0) {
          console.warn('[Participants] No valid participants to remove');
          return;
        }
        
        
        // Get the event GUID from the current URL (same way as in execute method)
        const router = service.injector.get(Router);
        const urlWithoutQuery = (router.url || '').split('?')[0];
        const pathSegments = urlWithoutQuery.split('/');
        const eventGuid = pathSegments.find(segment => 
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
        );
        
        if (!eventGuid) {
          console.error('[Participants] No event GUID found in URL');
          return;
        }
        
        // Call the remove participants API
        service.http.delete(`${environment.apiUrl}/events/${eventGuid}/participants`, {
          body: {
            participant_uuids: participantUuids
          }
        }).subscribe({
          next: (response: any) => {
            
            // Get the current data from the store
            service.store.select((state: any) => state.data?.[config.name]).pipe(
              take(1)
            ).subscribe((currentData: any) => {
              if (currentData && currentData.data && currentData.data.items) {
                // Filter out the removed participants from the current data
                const updatedItems = currentData.data.items.filter((item: any) => 
                  !participantUuids.includes(item.user?.uuid)
                );
                
                // Update the store with the filtered data
                const updatedData = {
                  ...currentData.data,
                  items: updatedItems,
                  totalCount: updatedItems.length
                };
                
                // Dispatch the updated data to the store
                service.store.dispatch(DataActions.loadDataSuccess({
                  dataSourceKey: config.name,
                  data: updatedData,
                  settings: currentData.settings || {}
                }));
                
              }
            });
          },
          error: (error: any) => {
            console.error('[Participants] Error removing participants:', error);
            // You might want to show a user-friendly error message here
          }
        });
      },
      tableOptions:
        config.columnProfile === 'compact'
          ? []
          : [
              {
                id: 'show_module_columns',
                label: 'Vis kolonner for moduler',
                type: 'toggle',
                default: false,
                onChange: (value: boolean) => {
                  showModuleColumns = value;
                },
              },
            ],
    }
  };
};

export const event_participants = (service: ComponentDataSourceService): DataSourceFunction =>
  createEventParticipantDataSource(service, {
    name: 'event_participants',
    description: 'Get event participants (excluding waitlist, instructors, and afbud)',
    participantFilter: (participant: EventParticipantDTO) =>
      participant.status !== 'waitlist' &&
      participant.role !== 'instructor' &&
      !isAfbudParticipationStatus(participant.status),
  });